import { useSelector } from "react-redux";
import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { pageBuildingHelpers } from "./logic/PageBuildingHelpers";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { trackPromise } from "react-promise-tracker";
import {
  selectUserData,
  selectUserWebsitePermissions,
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../../features/userSlice";
import { WebsitePermissions } from "../../../../data/user/WebsitePermissions";
import { WebsiteWithUuid } from "../../../../data/website/Website";
import store from "../../../../store";
import { pageBuildingEndPointLogic } from "../../../../logic/AssetManagement/PageManagement - Copy/PageBuilding/pageBuildingEndPointLogic";
import PageHeadlineDetails from "../,,/../../../../components/AssetManagement/PageBuilder/PageHeadlineDetails/PageHeadlineDetails";
import SharedStateContext from "../../../../components/SharedStateProvider";
import { selectSelectedPage } from "../../../../features/assetManagementSlice";
import PageBuilder from "../../../../components/AssetManagement/PageBuilder/PageBuilding/PageBuilder";
import { parseHtmlSectionsToComponents } from "../../../../components/AssetManagement/PageBuilder/utils/parseHtmlSectionToComponents";
import { COMPONENT_TYPES } from "../../../../components/AssetManagement/PageBuilder/utils/constants";
import ReactDOMServer from "react-dom/server";
import SimpleBackdrop from "../../../../components/BackDrop/BackDrop";
import { generateFinalContent, buildWidgetsPayload } from "../../../../components/AssetManagement/PageBuilder/PageBuilderHelpers/helpers";
import { useDispatch } from "react-redux";
import { Box } from "@mui/system";

export default function PageBuildingEditScreen(userToken: any) {
  // Basic state
  const [searchParams] = useSearchParams();
  const [pageContent, setPageContent] = useState<any>();
  const [pageUrlContent, setPageUrlContent] = useState<any>();
  const [, setPreview] = useState<any>(null);
  const [components, setComponents] = useState([]);
  const [currentPageElements, setCurrentPageElements] = useState<any>();
  const [currentPageElementsOriginal, setCurrentPageElementsOriginal] =
    useState();
  const siteUid = useMemo(
    () => searchParams.get("selectedSite"),
    [searchParams],
  ) as any;

  const allWebsiteList = useSelector(selectUserWebsitePermissions);
  let matchedSiteName: string | null = null;

  if (siteUid) {
    const matchedItem = allWebsiteList.find((siteObj: any) => siteObj[siteUid]);

    if (matchedItem) {
      matchedSiteName = matchedItem[siteUid].ui_site_name;
    }
  }
  // UI state
  const [isPublishOpen, setisPublishOpen] = useState<any>(false);
  const [isElementSaved, setIsElementSaved] = useState(false);
  const [ids, setIds] = useState<number[]>([]);

  // Page building state
  const [pageBuildingStructure, setPageBuildingStructure] = useState();
  const [pageDictionary, setPageDictionary] = useState<any>();
  const [siteAllAvailableWidgets, setSiteAllAvailableWidgets] = useState([]);
  const [pageUrlComponents, setPageUrlComponents] = useState<any>();
  const [header, setHeader] = useState<any>();
  const [headerMenu, setHeaderMenu] = useState<any>();
  const [footer, setFooter] = useState<any>();
  const [tasks, setTasks] = useState({}) as any;

  // Error handling and loading
  // Using a counter instead of a boolean so every retry click reliably triggers the effect,
  // regardless of the previous value (boolean toggle would no-op on the 2nd click).
  const [retryCount, setRetryCount] = useState(0);
  const [pageStructureError, setPageStructureError] = useState<string | null>(null);
  const [pageIsUpdatedCount, setPageIsUpdatedCount] = useState<number>();
  const [previewCurrentChanges, setPreviewCurrentChanges] = useState<any>([]);
  const [previewLink, setPreviewLink] = useState<any>("");
  const [activeInsertPosition, setActiveInsertPosition] = useState<
    "above" | "below" | null
  >(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedId, setSelectedId] = useState("") as any;

  // Refs for avoiding unnecessary re-renders
  const isInitialLoadRef = useRef(true);
  const loadingOperationsRef = useRef(new Set<string>());

  // Hooks
  const selectedPage = useSelector(selectSelectedPage);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { siteName } = useParams();

  const { pageRefreshStatus, setPageRefreshStatus } =
    useContext(SharedStateContext);

  // Selectors
  const userData = useSelector(selectUserData);
  const userWebsitePermissions = userData?.data
    ?.website_permissions as WebsitePermissions;
  const response = userData?.data?.products;

  // Memoized values - prevent unnecessary recalculations
  const oauthTokenHeader = useMemo(
    () => userToken?.userToken,
    [userToken?.userToken],
  );

  const pageUid = useMemo(
    () => searchParams.get("selectedPage"),
    [searchParams],
  ) as any;

  const selectedSite = useMemo(() => {
    if (!siteUid || !userWebsitePermissions) return null;
    return (
      (userWebsitePermissions.find(
        (website) => Object.keys(website)[0] === siteUid,
      ) as WebsiteWithUuid) ?? null
    );
  }, [siteUid, userWebsitePermissions]);

  const pageTitle = useMemo(() => {
    return pageContent?.post_title || selectedPage?.post_title;
  }, [pageContent?.post_title, selectedPage?.post_title]);

  const pageName = useMemo(() => {
    return pageContent?.post_name || selectedPage?.post_name;
  }, [pageContent?.post_name, selectedPage?.post_name]);

  const language = useMemo(() => {
    if (!pageDictionary) return null;
    try {
      return JSON.parse(pageDictionary)?.settings?.lang;
    } catch {
      return null;
    }
  }, [pageDictionary]);

  // Memoized loading state
  const isEssentialDataLoading = useMemo(() => {
    return !siteAllAvailableWidgets?.length || !pageDictionary || !pageContent;
  }, [siteAllAvailableWidgets?.length, pageDictionary, pageContent]);

  // Debounced error handler
  const handleError = useCallback((error: any, context?: string) => {
    const message =
      error instanceof Error
        ? error.message
        : error?.toString() || "Unknown error";
    store.dispatch(
      setDisplayErrorSnackBar({
        state: true,
        message: context ? `${context}: ${message}` : message,
        type: "error",
      }),
    );
    store.dispatch(setIsLoading(false));
  }, []);

  // API Functions - Separated for better control
  const loadPageUrlContent = useCallback(
    async (previewUrl: string) => {
      if (!previewUrl || loadingOperationsRef.current.has("urlContent")) return;

      loadingOperationsRef.current.add("urlContent");
      try {
        await pageBuildingEndPointLogic.getPageUrlContent(
          previewUrl,
          setPageUrlContent,
        );
        setShowSidebar(false);
        setSelectedId(null);
      } catch (e) {
        handleError(e, "Failed to load page URL content");
      } finally {
        loadingOperationsRef.current.delete("urlContent");
      }
    },
    [handleError],
  );

  const loadSiteWidgets = useCallback(async () => {
    if (!siteUid || loadingOperationsRef.current.has("siteWidgets")) return;

    loadingOperationsRef.current.add("siteWidgets");
    try {
      // Replace specific siteUid if it matches the old value
      const modifiedSiteUid =
        siteUid === "fc32450a45d44ab1ac94e84b5de73f06"
          ? "b4ae9c184e884cee976d593ebda9fff3"
          : siteUid;

      await pageBuildingEndPointLogic.getSiteAllAvailableWidgets(
        modifiedSiteUid,
        oauthTokenHeader,
        setSiteAllAvailableWidgets,
        () => {},
        () => setRetryCount((prev) => prev + 1),
        () => {},
        trackPromise,
        setIsLoading,
      );
    } catch (e) {
      handleError(e, "Failed to load site widgets");
    } finally {
      loadingOperationsRef.current.delete("siteWidgets");
    }
  }, [siteUid, oauthTokenHeader, handleError]);

  const loadPageContent = useCallback(async () => {
    if (!siteUid || !pageUid || loadingOperationsRef.current.has("pageContent"))
      return;

    loadingOperationsRef.current.add("pageContent");
    try {
      await pageBuildingEndPointLogic.getPageContent(
        siteUid,
        pageUid,
        oauthTokenHeader,
        setPageContent,
        () => {}, // setPageStructureDesign removed
        setPageDictionary,
        setIsLoading,
        () => {},
        () => setRetryCount((prev) => prev + 1),
        () => {},
        navigate,
        location,
      );
    } catch (e) {
      handleError(e, "Failed to load page content");
    } finally {
      loadingOperationsRef.current.delete("pageContent");
    }
  }, [siteUid, pageUid, oauthTokenHeader, navigate, location, handleError]);

  const initializePageStructure = useCallback(async () => {
    if (
      !pageContent ||
      !pageUrlContent ||
      !pageDictionary ||
      !siteAllAvailableWidgets?.length ||
      loadingOperationsRef.current.has("pageStructure")
    )
      return;

    loadingOperationsRef.current.add("pageStructure");
    // Clear any previous structure error on each attempt
    setPageStructureError(null);
    try {
      store.dispatch(setIsLoading(true));

      pageBuildingHelpers.initializePageBuilder(
        setCurrentPageElementsOriginal,
        pageDictionary,
        pageContent,
        pageUrlContent,
        setHeader,
        setHeaderMenu,
        setFooter,
        setPageUrlComponents,
        setPageBuildingStructure,
        tasks,
        setTasks,
        siteAllAvailableWidgets,
        setCurrentPageElements,
      );
    } catch (e) {
      console.error("Page Builder Structure Error:", e);
      const message =
        "The page template structure doesn't match the expected format. Please verify the template and try again.";
      setPageStructureError(message);
      // Error is already shown in the dialog — no snackbar needed
    } finally {
      loadingOperationsRef.current.delete("pageStructure");
      store.dispatch(setIsLoading(false));
    }
  }, [
    pageContent,
    pageUrlContent,
    pageDictionary,
    siteAllAvailableWidgets,
    tasks,
  ]);

  const handlePreviewChanges = useCallback(async () => {
    if (
      Object.values(previewCurrentChanges)?.length !== 2 ||
      loadingOperationsRef.current.has("previewChanges")
    )
      return;

    loadingOperationsRef.current.add("previewChanges");
    try {
      store.dispatch(setIsLoading(true));
      pageBuildingHelpers.updateWidgetChangedWithPreviewCurrentChanges(
        previewCurrentChanges,
        pageBuildingStructure,
        currentPageElements,
        setCurrentPageElements,
      );
    } catch (e) {
      handleError(e, "Cannot parse new widget changes from preview");
    } finally {
      loadingOperationsRef.current.delete("previewChanges");
      store.dispatch(setIsLoading(false));
    }
  }, [
    previewCurrentChanges,
    pageBuildingStructure,
    currentPageElements,
    handleError,
  ]);

  // Initialize data on mount and when dependencies change
  const initializeAllData = useCallback(async () => {
    if (!pageUid) {
      pageBuildingEndPointLogic.navigateTo404(navigate);
      return;
    }

    if (!siteUid) {
      console.error("Selected site is missing");
      return;
    }

    try {
      store.dispatch(setIsLoading(true));

      // Load initial data
      await Promise.all([loadSiteWidgets(), loadPageContent()]);
    } catch (e) {
      handleError(e, "Failed to load initial data");
    } finally {
      store.dispatch(setIsLoading(false));
    }
  }, [
    pageUid,
    siteUid,
    loadSiteWidgets,
    loadPageContent,
    navigate,
    handleError,
  ]);

  // Initial load effect — retryCount increments on every retry click, so this always fires
  useEffect(() => {
    if (isInitialLoadRef.current || retryCount > 0 || pageRefreshStatus) {
      isInitialLoadRef.current = false;
      initializeAllData();
      if (pageRefreshStatus) {
        setPageRefreshStatus(false);
      }
    }
  }, [initializeAllData, retryCount, pageRefreshStatus, setPageRefreshStatus]);

  // Update preview when pageContent changes and load URL content
  useEffect(() => {
    const newPreview = pageContent?.preview_link;
    if (newPreview) {
      setPreview(newPreview);
      // Always load page URL content when pageContent changes (not just when preview URL changes)
      loadPageUrlContent(newPreview);
    }
  }, [pageContent, loadPageUrlContent]);

  // Initialize page structure when all required data is available
  useEffect(() => {
    initializePageStructure();
  }, [initializePageStructure]);

  // Handle preview changes
  useEffect(() => {
    handlePreviewChanges();
  }, [handlePreviewChanges]);
  // eslint-disable-next-line

  // Parse components when page elements change
  useEffect(() => {
    if (!currentPageElements) return;

    const html = ReactDOMServer.renderToStaticMarkup(currentPageElements);
    const parsedComponents = parseHtmlSectionsToComponents(html);
    const filteredComponents = parsedComponents.filter((component) => {
      if (component.type !== COMPONENT_TYPES.WIDGET) return true;
      return component.props.className?.includes("internal-widget");
    }) as any;

    setComponents(filteredComponents);
  }, [currentPageElements]);

  // Update element saved state
  useEffect(() => {
    if (!pageContent || !currentPageElements) return;

    const status = pageContent.post_status;
    if (status === "draft" || status === "pending") {
      setIsElementSaved(false);
    } else if (status === "publish") {
      setIsElementSaved(false);
      setisPublishOpen(false);
    }
    // eslint-disable-next-line
  }, [pageContent?.post_status, currentPageElements]);

  // Stable callback functions with integrated URL content loading
  const previewCurrentChange = useCallback(
    async (elementIndex: number) => {
      if (loadingOperationsRef.current.has("previewCurrentChange")) return;

      loadingOperationsRef.current.add("previewCurrentChange");
      try {
        store.dispatch(setIsLoading(true));

        const pageContentJson = generateFinalContent(
          components,
          siteAllAvailableWidgets,
          language,
        );

        const data = {
          data: {
            post_content: pageContentJson,
          },
        };

        await pageBuildingEndPointLogic.getPagePreviewForCurrentChanges(
          elementIndex,
          siteUid,
          pageUid,
          data,
          setIsLoading,
          trackPromise,
          setPreviewCurrentChanges,
          oauthTokenHeader,
          setPreviewLink,
        );
      } catch (e) {
        handleError(e, "Failed to generate preview");
      } finally {
        loadingOperationsRef.current.delete("previewCurrentChange");
        store.dispatch(setIsLoading(false));
      }
    },
    [
      components,
      siteAllAvailableWidgets,
      language,
      siteUid,
      pageUid,
      oauthTokenHeader,
      handleError,
    ],
  );

  const publishPage = useCallback(async () => {
    if (loadingOperationsRef.current.has("publishPage")) return;

    loadingOperationsRef.current.add("publishPage");
    try {
      store.dispatch(setIsLoading(true));

      const pageContentJson = generateFinalContent(
        components,
        siteAllAvailableWidgets,
        language,
      );

      const widgetsPayload = buildWidgetsPayload(components, siteAllAvailableWidgets);

      const updateObject = {
        data: {
          post_title: pageContent?.post_title,
          post_content: pageContentJson,
          post_status: "publish",
          widgets: widgetsPayload,
        },
      };

      await pageBuildingEndPointLogic.updatePage(
        updateObject,
        siteUid,
        pageUid,
        dispatch,
        oauthTokenHeader,
        setPageIsUpdatedCount,
        pageIsUpdatedCount,
        () => {},
      );

      if (pageContent?.unpublished_change === true) {
        await pageBuildingEndPointLogic.discardPage(
          siteUid,
          pageUid,
          setIsLoading,
          oauthTokenHeader,
          () => {},
          "",
          setPageIsUpdatedCount,
          pageIsUpdatedCount,
          () => {},
        );
      }

      setPageRefreshStatus(true);

      // Reload page content to get the fresh preview link
      await loadPageContent();
    } catch (e) {
      handleError(e, "Failed to publish page");
    } finally {
      loadingOperationsRef.current.delete("publishPage");
      store.dispatch(setIsLoading(false));
    }
  }, [
    components,
    siteAllAvailableWidgets,
    language,
    pageContent,
    siteUid,
    pageUid,
    dispatch,
    oauthTokenHeader,
    pageIsUpdatedCount,
    setPageRefreshStatus,
    loadPageContent,
    handleError,
  ]);

  const addId = useCallback((newId: number) => {
    setIds((prev) => (prev.includes(newId) ? prev : [...prev, newId]));
  }, []);

  const savePage = useCallback(async () => {
    if (loadingOperationsRef.current.has("savePage")) return;

    loadingOperationsRef.current.add("savePage");
    try {
      store.dispatch(setIsLoading(true));
      const pageContentJson = generateFinalContent(
        components,
        siteAllAvailableWidgets,
        language,
      );

      const updateObject = {
        data: {
          post_title: pageContent?.post_title,
          post_content: pageContentJson,
          post_status: "pending",
        },
      };

      await pageBuildingEndPointLogic.savePage(
        updateObject,
        siteUid,
        pageUid,
        dispatch,
        oauthTokenHeader,
        setPageIsUpdatedCount,
        pageIsUpdatedCount,
        () => {},
      );

      setIsElementSaved(false);
      if (pageContent?.page_id) {
        addId(pageContent.page_id);
      }

      if (pageContent?.shared_uid !== undefined) {
        setPageRefreshStatus(true);
      }

      // Reload page content to get the fresh preview link
      await loadPageContent();
    } catch (e) {
      handleError(e, "Failed to save page");
    } finally {
      loadingOperationsRef.current.delete("savePage");
      store.dispatch(setIsLoading(false));
    }
  }, [
    components,
    siteAllAvailableWidgets,
    language,
    pageContent,
    siteUid,
    pageUid,
    dispatch,
    oauthTokenHeader,
    pageIsUpdatedCount,
    addId,
    setPageRefreshStatus,
    loadPageContent,
    handleError,
  ]);

  const unpublishPage = useCallback(async () => {
    if (loadingOperationsRef.current.has("unpublishPage")) return;

    loadingOperationsRef.current.add("unpublishPage");
    try {
      store.dispatch(setIsLoading(true));

      const pageContentJson = generateFinalContent(
        components,
        siteAllAvailableWidgets,
        language,
      );

      const updateObject = {
        data: {
          post_title: pageContent?.post_title,
          post_content: pageContentJson,
          post_status: "draft",
        },
      };

      await pageBuildingEndPointLogic.unpublishPage(
        updateObject,
        siteUid,
        pageUid,
        dispatch,
        oauthTokenHeader,
        setPageIsUpdatedCount,
        pageIsUpdatedCount,
        () => {},
      );

      setIsElementSaved(false);
      if (pageContent?.page_id) {
        addId(pageContent.page_id);
      }

      if (pageContent?.shared_uid !== undefined) {
        setPageRefreshStatus(true);
      }

      // Reload page content to get the fresh preview link
      await loadPageContent();
    } catch (e) {
      handleError(e, "Failed to unpublish page");
    } finally {
      loadingOperationsRef.current.delete("unpublishPage");
      store.dispatch(setIsLoading(false));
    }
  }, [
    components,
    siteAllAvailableWidgets,
    language,
    pageContent,
    siteUid,
    pageUid,
    dispatch,
    oauthTokenHeader,
    pageIsUpdatedCount,
    addId,
    setPageRefreshStatus,
    loadPageContent,
    handleError,
  ]);

  const discardPage = useCallback(async () => {
    if (loadingOperationsRef.current.has("discardPage")) return;

    loadingOperationsRef.current.add("discardPage");
    try {
      await pageBuildingEndPointLogic.discardPage(
        siteUid,
        pageUid,
        dispatch,
        oauthTokenHeader,
        () => {},
        "",
        setPageIsUpdatedCount,
        pageIsUpdatedCount,
        () => {},
      );

      setPageRefreshStatus(true);

      // Reload page content to get the fresh preview link
      await loadPageContent();
    } catch (error) {
      handleError(error, "Failed to discard page");
    } finally {
      loadingOperationsRef.current.delete("discardPage");
      store.dispatch(setIsLoading(false));
    }
    // eslint-disable-next-line
  }, [
    selectedPage,
    siteUid,
    pageUid,
    dispatch,
    oauthTokenHeader,
    pageIsUpdatedCount,
    setPageRefreshStatus,
    loadPageContent,
    handleError,
  ]);

  // Memoized props objects
  const siteBaseUrl = useMemo(() => {
    if (!pageContent?.preview_link) return "";
    try {
      return new URL(pageContent.preview_link).origin;
    } catch {
      return "";
    }
  }, [pageContent?.preview_link]);

  const pageBuildingProps = useMemo(
    () => ({
      pageUrlComponents,
      pageBuildingStructure,
      header,
      footer,
      headerMenu,
      htmlWrapperDiv: "",
      siteAllAvailableWidgets,
      oauthTokenHeader,
      siteUid,
      setisPublishOpen,
      setIsElementSaved,
      response,
      pageContent,
      pageDictionary,
      currentPageElements,
      components,
      setComponents,
      activeInsertPosition,
      setActiveInsertPosition,
      setShowSidebar,
      showSidebar,
      setSelectedId,
      selectedId,
      siteBaseUrl,
    }),
    [
      pageUrlComponents,
      pageBuildingStructure,
      header,
      footer,
      headerMenu,
      siteAllAvailableWidgets,
      oauthTokenHeader,
      siteUid,
      response,
      pageContent,
      pageDictionary,
      currentPageElements,
      components,
      activeInsertPosition,
      showSidebar,
      selectedId,
      siteBaseUrl,
    ],
  );

  const PageHeadlineDetailsProps = useMemo(
    () => ({
      oauthTokenHeader,
      siteUid,
      pageUid,
      pageName,
      siteName,
      publishPage,
      savePage,
      selectedSite,
      previewLink,
      pageContent,
      previewCurrentChange,
      discardPage,
      currentPageElements,
      isPublishOpen,
      isElementSaved,
      pageContentPageId: pageContent?.page_id,
      ids,
      setPageContent,
      setPageDictionary,
      setPageUrlContent,
      setSiteAllAvailableWidgets,
      siteAllAvailableWidgets,
      setPageRefreshStatus,
      currentPageElementsOriginal,
      response,
      pageTitle,
      unpublishPage,
      components,
      setComponents,
      matchedSiteName,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      oauthTokenHeader,
      siteUid,
      pageUid,
      pageName,
      siteName,
      publishPage,
      savePage,
      selectedSite,
      previewLink,
      pageContent,
      previewCurrentChange,
      discardPage,
      currentPageElements,
      isPublishOpen,
      isElementSaved,
      pageContent?.page_id,
      ids,
      siteAllAvailableWidgets,
      currentPageElementsOriginal,
      response,
      pageTitle,
      unpublishPage,
      components,
    ],
  );

  return (
    <>
      <Dialog
        open={!!pageStructureError}
        aria-labelledby="page-load-error-title"
        aria-describedby="page-load-error-description"
        disableEscapeKeyDown
      >
        <DialogTitle id="page-load-error-title">
          Page Could Not Be Loaded
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="page-load-error-description">
            {pageStructureError}
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            If this issue persists, verify the page template matches the
            expected structure and try again.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2, mb: 2, py: 0 }}>
          <Button color="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              setPageStructureError(null);
              // Clear cached URL content so initializePageStructure waits
              // for fresh data before attempting to parse again
              setPageUrlContent(undefined);
              setRetryCount((prev) => prev + 1);
            }}
          >
            Retry
          </Button>
        </DialogActions>
      </Dialog>

      {isEssentialDataLoading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <PageHeadlineDetails {...PageHeadlineDetailsProps} />
          </Box>
          <PageBuilder {...pageBuildingProps} />
        </>
      )}
    </>
  );
}
